/**
 * 상담 플로우 라이브러리 — 문서 한 벌.
 *
 * 시드 스크립트가 이 목록을 Firestore `playground_flows` 에 올리고, 프리뷰는 거기서만
 * 문서를 읽는다. 목록이 한 곳에 있어야 "지금 라이브러리에 무엇이 있는가" 와 "코드가
 * 무엇을 정의하는가" 가 갈라지지 않는다.
 *
 * 루트(`counsel-full-flow`)를 포함한 전부가 여기 있다. 루트만 받아 실행하면 나머지는
 * 체인을 따라 자동으로 끌려온다.
 */
import type { AssetDocument } from "@miso/design-system";

import { ADDITIONAL_SPACES } from "./additionalSpaces";
import { COUNSEL_COMPLETE } from "./counselComplete";
import { COUNSEL_FULL_FLOW } from "./counselFullFlow";
import { COUNSEL_REVIEW } from "./counselReview";
import { HOUSE_STRUCTURE_DONE } from "./houseStructureDone";
import { MOVING_ITEMS } from "./movingItems";
import { MOVING_TYPES } from "./movingTypes";
import { PICK_ROOM } from "./pickRoom";
import { SKETCH_VIDEO } from "./sketchVideo";
import { COUNSEL_START, HOUSE_STRUCTURE, PROGRESS_DOCUMENTS } from "./stageFlows";

/** 라이브러리 항목 하나. `title` 은 에디터 목록에 뜨는 이름이다. */
export interface CounselLibraryEntry {
  id: string;
  title: string;
  doc: AssetDocument;
  /** 문서 하나만 따로 띄워 볼 때의 표본값. 전체 주행에서는 쓰이지 않는다. */
  initialValues?: Record<string, unknown>;
}

function entry(
  title: string,
  bundle: { doc: AssetDocument; initialValues?: Record<string, unknown> },
): CounselLibraryEntry {
  return {
    id: bundle.doc.id,
    title,
    doc: bundle.doc,
    ...(bundle.initialValues ? { initialValues: bundle.initialValues } : {}),
  };
}

export const COUNSEL_LIBRARY: readonly CounselLibraryEntry[] = [
  entry("상담 전체 플로우", COUNSEL_FULL_FLOW),
  entry("상담 시작", COUNSEL_START),
  entry("집 구조 기록", HOUSE_STRUCTURE),
  entry("집 구조 완료", HOUSE_STRUCTURE_DONE),
  entry("방 선택", PICK_ROOM),
  entry("짐 기록", MOVING_ITEMS),
  entry("이사 날짜", { doc: PROGRESS_DOCUMENTS["moving-date"] }),
  entry("출발지 운송수단", { doc: PROGRESS_DOCUMENTS["departure-transport"] }),
  entry("도착지 주소", { doc: PROGRESS_DOCUMENTS["arrival-address"] }),
  entry("도착지 운송수단", { doc: PROGRESS_DOCUMENTS["arrival-transport"] }),
  entry("포장 방식", { doc: PROGRESS_DOCUMENTS.packing }),
  entry("이사 유형", MOVING_TYPES),
  entry("고객 요청사항", { doc: PROGRESS_DOCUMENTS["customer-requests"] }),
  entry("상담 리뷰", COUNSEL_REVIEW),
  entry("집 스케치 영상", SKETCH_VIDEO),
  entry("추가공간", ADDITIONAL_SPACES),
  entry("상담 완료", COUNSEL_COMPLETE),
];

const byId = new Map(COUNSEL_LIBRARY.map((e) => [e.id, e]));

/** 라이브러리에 없는 문서를 가리키는 체인이 있으면 여기서 드러난다. */
export function counselLibraryEntry(id: string): CounselLibraryEntry | undefined {
  return byId.get(id);
}
